// Best-effort starter props for a NEW section, derived by walking the block's
// Zod schema. Fills required fields with empty placeholders (+ one sample item
// for arrays) and skips optionals, so a freshly added section shows editable
// fields the user can fill in. Save validates against the real schema.

function build(s: any): any {
  const def = s?._def;
  switch (def?.typeName) {
    case 'ZodObject': {
      const shape = typeof s.shape === 'function' ? s.shape() : s.shape;
      const out: Record<string, unknown> = {};
      for (const key of Object.keys(shape)) {
        const v = build(shape[key]);
        if (v !== undefined) out[key] = v;
      }
      return out;
    }
    case 'ZodString':
      return '';
    case 'ZodNumber':
      return 0;
    case 'ZodBoolean':
      return false;
    case 'ZodArray': {
      const el = build(def.type);
      return el === undefined ? [] : [el];
    }
    case 'ZodOptional':
      // Include optional fields (e.g. images) so they show up as editable
      // placeholders — hiding them meant editors couldn't add images.
      return build(def.innerType);
    case 'ZodNullable':
      return build(def.innerType);
    case 'ZodDefault':
      try {
        return def.defaultValue();
      } catch {
        return build(def.innerType);
      }
    case 'ZodEnum':
      return def.values?.[0] ?? '';
    case 'ZodEffects':
      return build(def.schema);
    default:
      return undefined;
  }
}

export function starterProps(schema: unknown): Record<string, unknown> {
  const v = build(schema);
  return v && typeof v === 'object' ? v : {};
}

// Merge the schema's full field set into existing content so the editor shows
// EVERY editable field (incl. optional images) even for sections that were
// saved without them. Existing values always win; arrays fill each item from
// the schema's item template without adding/removing items.
function merge(template: unknown, actual: unknown): unknown {
  if (Array.isArray(actual)) {
    const item = Array.isArray(template) ? template[0] : undefined;
    return item !== undefined ? actual.map((a) => merge(item, a)) : actual;
  }
  if (template && typeof template === 'object' && actual && typeof actual === 'object') {
    const out: Record<string, unknown> = { ...(actual as object) };
    for (const k of Object.keys(template as object)) {
      out[k] = merge((template as any)[k], (actual as any)[k]);
    }
    return out;
  }
  // A list item whose schema became an object but was saved as a scalar (e.g.
  // footer links upgraded from bare strings to {label,href,newTab}) — wrap the
  // scalar into the object's first text field so the editor shows the new fields
  // pre-filled instead of a lone text box.
  if (template && typeof template === 'object' && !Array.isArray(template) && actual != null && typeof actual !== 'object') {
    const tpl = template as Record<string, unknown>;
    const key = Object.keys(tpl).find((k) => typeof tpl[k] === 'string');
    return key ? { ...tpl, [key]: actual } : tpl;
  }
  return actual === undefined ? template : actual;
}

export function withSchemaFields(schema: unknown, props: Record<string, unknown>): Record<string, unknown> {
  return merge(starterProps(schema), props) as Record<string, unknown>;
}
